import { useId, useState } from "react";
import { Button } from "../../../../components/Button";
import { TextInput } from "../../../../components/TextInput";
import { createItineraryRequest } from "../../../../services/itinerary";
import type { ItineraryRequest } from "../../../../types/itinerary";
import { getErrorMessage } from "../../../../utils/errorHandler";
import { formatEventDateRange } from "./eventDateRange";
import { ItineraryRequestConfirmation } from "./ItineraryRequestConfirmation";
import "./Itinerary.css";

interface RequestItineraryFromEventProps {
  /** The tournament already on the org's calendar that the request is raised from. */
  event: {
    id: string;
    title: string;
    startsAtUtc: string;
    endsAtUtc: string;
    isAllDay: boolean;
  };
}

/**
 * "Ask RosterPilot for this itinerary" from a tournament ALREADY on the calendar.
 *
 * The request carries only `calendarEventId` (plus the optional link and notes). The server
 * derives the tournament name and dates from that event and ignores anything sent alongside them,
 * so re-asking the coach to type a name and dates the calendar already holds would be worse than
 * redundant — it would invite a request whose text disagrees with the event it points at. The
 * event's name and dates are shown read-only instead, so the coach can see what will be sent.
 */
export const RequestItineraryFromEvent = ({ event }: RequestItineraryFromEventProps) => {
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const [scheduleUrl, setScheduleUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState<ItineraryRequest | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const created = await createItineraryRequest({
        calendarEventId: event.id,
        // Only ever added when the coach actually supplied one, so the plain "from this event"
        // request is exactly `{ calendarEventId }` and nothing can compete with the event.
        ...(scheduleUrl.trim() ? { scheduleUrl: scheduleUrl.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setSubmitted(created);
    } catch (err) {
      // The server's message names the offending schedule link; escaping it would render the URL
      // as HTML entities. React already escapes interpolated text.
      setError(getErrorMessage(err, "Couldn't send the request. Try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="event-view__section" data-testid="request-itinerary-section">
        <ItineraryRequestConfirmation
          request={submitted}
          onDone={() => {
            setSubmitted(null);
            setOpen(false);
            setScheduleUrl("");
            setNotes("");
          }}
        />
      </div>
    );
  }

  return (
    <div className="event-view__section" data-testid="request-itinerary-section">
      <div className="event-view__label">Itinerary</div>
      {!open ? (
        <>
          <p className="event-editor__hint">
            Don't have the game-by-game schedule for this tournament? Ask RosterPilot to build it.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(true)}
            data-testid="request-itinerary-cta"
          >
            Request an itinerary for this tournament
          </Button>
        </>
      ) : (
        <form
          className="itin-request itin-request--inline"
          data-testid="request-itinerary-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p className="itin-request__blurb">
            We'll build the itinerary for{" "}
            <strong data-testid="request-itinerary-event-name">{event.title}</strong>,{" "}
            <span data-testid="request-itinerary-event-dates">
              {formatEventDateRange(event)}
            </span>
            .
          </p>

          <TextInput
            className="itin-field"
            label={
          <>
            Schedule link <span className="itin-field__optional">(optional)</span>
          </>
        }
            id={`${fieldId}-url`}
            type="url"
            value={scheduleUrl}
            onChange={(e) => setScheduleUrl(e.target.value)}
            placeholder="https://…/schedule"
            data-testid="request-itinerary-url"
          />

          <div className="itin-field">
            <label className="itin-label" htmlFor={`${fieldId}-notes`}>
              Notes <span className="itin-field__optional">(optional)</span>
            </label>
            <textarea
              id={`${fieldId}-notes`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Anything we should know"
              data-testid="request-itinerary-notes"
            />
          </div>

          {error && (
            <div className="event-editor__error" role="alert" data-testid="request-itinerary-error">
              {error}
            </div>
          )}

          <div className="itin-request__actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              data-testid="request-itinerary-submit"
            >
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
